/**
 * The operator-facing control for the enforced thumbnail style in `config.central.images`.
 *
 * It owns two keys of that document — `game.default-style` and the chosen style's
 * `game.style-sizes` entry — and hands the rewritten document back to its parent, which owns the
 * save. Everything else on screen (the readings, the preview, the verdict) is guidance: the size the
 * operator records has to describe the images that actually exist.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { CardBox } from "../../RouterView";
import { getContainer } from "../../../ioc/IOCSetup";
import { t } from "../../../i18n";
import { AssetDimensions, resolveAssetUrl } from "./assetSample";
import {
  documentBasis,
  EnforcedStyleSelection,
  parseCentralImagesDocument,
  readCentralImagesEnabled,
  readStyleTemplate,
  toStyleOptions,
  writeEnforcedStyle,
} from "./centralImagesDocument";
import { DocumentNotices } from "./DocumentNotices";
import { resolveEnforcedStyleVerdict } from "./enforcedStyleVerdict";
import { AssetMeasurementOutcome, measureAssetSize } from "./measureAssetSize";
import { MeasurementNotices } from "./MeasurementNotices";
import { Draft, draftForStyle, draftFromDocument, isSizeFieldRefused, MAX_SIZE, NO_STYLE, readDraftSize, StyleChoice } from "./sizeDraft";
import { CANDIDATE_LIMIT, sampleSetKeyOf, SampleCandidate, samplesForStyle, toCandidates } from "./styleSamples";
import { TilePreview } from "./TilePreview";
import { VerdictNotice } from "./VerdictNotice";

interface Props {
  /** The raw `JsonDocument.json` text the parent is editing. */
  json: string;
  /** `JsonDocument.locked` — a locked document cannot be changed here. */
  locked: boolean;
  /** Receives the rewritten document text; the parent decides when it reaches the server. */
  onApply: (json: string) => void;
}

/** A draft together with the document it was formed against. */
interface PinnedDraft {
  basis: string;
  draft: Draft;
}

/** A reading together with what it was taken of. */
interface PinnedMeasurement {
  identity: string;
  outcome: AssetMeasurementOutcome;
}

export function EnforcedThumbnailStyleControl({ json, locked, onApply }: Props): JSX.Element {
  const centralImages = useMemo(() => parseCentralImagesDocument(json), [json]);
  const documentUnreadable = centralImages === null;
  const basis = useMemo(() => documentBasis(centralImages), [centralImages]);

  /** The draft in force for the document on screen: a pinned draft only while its basis still matches. */
  const draftInForce = (pinned: PinnedDraft | undefined): Draft => (pinned?.basis === basis ? pinned.draft : draftFromDocument(centralImages));

  // Until the operator touches the control, the document is what it shows; from the first change on
  // the draft is. Deriving rather than syncing keeps the parent's own edits to the raw json — which
  // arrive on every keystroke of its textarea — from wiping a size the operator is halfway through
  // typing here. Pinning the draft to the document it was formed against is what keeps that from
  // reaching further: the parent replaces the document under this control without remounting it, and
  // a draft describing the document that was there is not one to apply to the document that is.
  const [pinnedDraft, setPinnedDraft] = useState<PinnedDraft | undefined>(undefined);
  const current = draftInForce(pinnedDraft);

  // Undefined until the catalogue lookup reports back. An empty list would otherwise read the same
  // as a lookup still in flight, and the two owe the operator opposite answers: silence while the
  // games are still coming, and "there is nothing here to measure" once they have.
  const [candidates, setCandidates] = useState<SampleCandidate[] | undefined>(undefined);
  const [samplesFailed, setSamplesFailed] = useState(false);
  const [measuring, setMeasuring] = useState(false);
  const [pinnedMeasurement, setPinnedMeasurement] = useState<PinnedMeasurement | undefined>(undefined);

  // Reading the images is several cross-origin loads and the operator can change style while one
  // runs. Only the newest may publish: an older reading describes a template no longer on screen,
  // and writing it back would silently revert the selection just made.
  const measurementGeneration = useRef(0);

  // One catalogue page is fetched per mount at most, and only once there is something for it to
  // serve. The host remounts this control on every version change and every Reset, so a lookup made
  // regardless would pay for fifty games on a document nobody is configuring a style in.
  const catalogueLookup = useRef({ issued: false, abandoned: false });

  // The document the newest render describes, which is how a reading that comes back later tells
  // that the document it was taken against is gone.
  const latestBasis = useRef(basis);

  const selectedStyle = current.style === NO_STYLE ? undefined : current.style;
  const styleOptions = toStyleOptions(centralImages, selectedStyle);
  const recordedTemplate = selectedStyle === undefined ? undefined : readStyleTemplate(centralImages, selectedStyle);

  // A blank template resolves to a url with no asset behind it, and the server ignores such a style
  // outright, so nothing downstream of here treats one as a template it can read or preview.
  const template = recordedTemplate === undefined || recordedTemplate.trim() === "" ? undefined : recordedTemplate;

  const samples = samplesForStyle(candidates ?? [], selectedStyle);
  const draftSize = readDraftSize(current);

  const styleInEffect = !locked && selectedStyle !== undefined && template !== undefined;

  // Watching the sample list itself would read the same images without end, each reading's own state
  // change being one more render of it.
  const measurementTrigger = styleInEffect && samples.length > 0 ? sampleSetKeyOf(selectedStyle, samples) : undefined;

  // What a reading describes and all it describes: the style, the images it covered, and the template
  // they were served from. The operator's own Apply records the very size a conflict is measured
  // against, so a reading still true of those images must not read as resolved by pressing it.
  const measurementIdentity =
    template === undefined || selectedStyle === undefined || samples.length === 0 ? undefined : `${template}|${sampleSetKeyOf(selectedStyle, samples)}`;

  /** The reading in force: a published reading only while what it was taken of is still what is on screen. */
  const measurement = pinnedMeasurement !== undefined && pinnedMeasurement.identity === measurementIdentity ? pinnedMeasurement.outcome : undefined;

  // A wait with a real operation behind it: the samples are still coming. A locked document fetches
  // nothing and has its own notice for why, so it is not waiting for anything.
  const readingPending = styleInEffect && candidates === undefined && !samplesFailed;

  useEffect(() => {
    const lookup = catalogueLookup.current;
    // A re-run for a changed selection is not an unmount, so a lookup already in flight stays wanted.
    lookup.abandoned = false;

    if (!locked && selectedStyle !== undefined && !lookup.issued) {
      lookup.issued = true;
      getContainer()
        .getCasinoGameService()
        .list({ limit: CANDIDATE_LIMIT, offset: 0 })
        .then((response) => {
          if (!lookup.abandoned) {
            setCandidates(toCandidates(response.items ?? []));
          }
        })
        .catch((error: unknown) => {
          if (!lookup.abandoned) {
            // The operator only ever sees "enter the sizes yourself", so the reason the lookup failed
            // has to reach the console or a report of a greyed-out button has nothing behind it.
            console.error("Unable to load sample games for the enforced thumbnail style", error);
            setSamplesFailed(true);
          }
        });
    }

    return () => {
      lookup.abandoned = true;
    };
  }, [locked, selectedStyle]);

  useEffect(() => {
    latestBasis.current = basis;
  }, [basis]);

  // A reading in flight outlives the control: each image waits up to its own deadline, so unmounting
  // mid-read would otherwise land on a component that is gone. Retiring the generation is what the
  // reading already checks itself against, so an unmount reads as one more superseded attempt.
  useEffect(
    () => () => {
      measurementGeneration.current += 1;
    },
    [],
  );

  const sizeRefused = selectedStyle !== undefined && (isSizeFieldRefused(current.width) || isSizeFieldRefused(current.height));

  const verdict = resolveEnforcedStyleVerdict({ defaultStyle: selectedStyle, size: draftSize, urlTemplate: template });
  const centralImagesEnabled = readCentralImagesEnabled(centralImages);

  /** Every write the operator makes goes through here, so no draft they form can be held without its document. */
  const updateDraftFromOperator = (next: (previous: Draft) => Draft): void => {
    setPinnedDraft((pinned) => ({ basis, draft: next(draftInForce(pinned)) }));
  };

  /**
   * A write nobody asked for, which stands down rather than re-pin. A draft formed against a
   * document since replaced is only suppressed, and comes back the moment that document does; taking
   * the document-derived draft that is standing in for it and pinning that at the live basis would
   * leave the operator nothing to come back to. An operator acting against what is on screen is
   * theirs to replace, so only this caller declines.
   *
   * The pinned draft is read inside the updater because the reading outlives the render that started
   * it: what that render saw pinned is a whole reading out of date by the time the answer arrives.
   */
  const updateDraftUnasked = (next: (previous: Draft) => Draft): void => {
    setPinnedDraft((pinned) => (pinned !== undefined && pinned.basis !== basis ? pinned : { basis, draft: next(draftInForce(pinned)) }));
  };

  const changeStyle = (value: string): void => {
    const style: StyleChoice = styleOptions.find((option) => option.style === value)?.style ?? NO_STYLE;
    measurementGeneration.current += 1;
    setMeasuring(false);
    updateDraftFromOperator(() => draftForStyle(centralImages, style));
    setPinnedMeasurement(undefined);
  };

  const adoptDimensions = (dimensions: AssetDimensions): void => {
    updateDraftFromOperator((previous) => ({ ...previous, width: String(dimensions.width), height: String(dimensions.height) }));
  };

  const runMeasurement = async (): Promise<void> => {
    if (template === undefined || measurementIdentity === undefined) {
      return;
    }
    measurementGeneration.current += 1;
    const generation = measurementGeneration.current;
    setMeasuring(true);
    const outcome = await measureAssetSize(template, samples);
    if (measurementGeneration.current !== generation) {
      return;
    }
    setMeasuring(false);
    setPinnedMeasurement({ identity: measurementIdentity, outcome });

    if (outcome.status !== "agreed") {
      return;
    }

    // The generation covers a style the operator changed while the images loaded; the document
    // changing under the reading is the other way it goes stale, and one this would otherwise answer
    // by filling in a size for a style the document now on screen may hold no template for.
    if (latestBasis.current !== basis) {
      return;
    }

    // A size the operator already recorded or typed is a deliberate value, so an agreed reading only
    // fills empty fields; the notices offer the reading instead of taking the decision away — the
    // conflict notice where the fields hold a size, the agreed notice where they hold none this
    // control can read. Each field is judged on its own, because a width typed while the other field
    // is still blank is no less deliberate than a complete pair. The fields are read back here rather
    // than captured, because they can be typed into while the images are still loading.
    updateDraftUnasked((previous) => ({
      ...previous,
      width: previous.width.trim() === "" ? String(outcome.dimensions.width) : previous.width,
      height: previous.height.trim() === "" ? String(outcome.dimensions.height) : previous.height,
    }));
  };

  // The newest render's reading. It closes over the basis, which moves with every keystroke the
  // parent makes in its editor, so a trigger holding the function itself would take a reading over an
  // edit made elsewhere in the document.
  const latestReading = useRef(runMeasurement);
  useEffect(() => {
    latestReading.current = runMeasurement;
  });

  // A size describing something other than the images is worth catching while there is still a
  // decision to make about it, so the operator does not have to ask. It goes through
  // `runMeasurement` rather than around it: the generation that captures is the only thing keeping a
  // reading of a style left behind from publishing over the one in effect now.
  useEffect(() => {
    if (measurementTrigger === undefined) {
      return;
    }
    void latestReading.current();
  }, [measurementTrigger]);

  const applySelection = (): void => {
    if (selectedStyle !== undefined && draftSize === undefined) {
      return;
    }
    const selection: EnforcedStyleSelection =
      selectedStyle === undefined || draftSize === undefined ? { kind: "none" } : { kind: "style", style: selectedStyle, ...draftSize };
    const rewritten = writeEnforcedStyle(json, selection);
    if (rewritten !== null) {
      onApply(rewritten);
    }
  };

  // A size that is not two numbers cannot be written at all, so the button waits for them; a size
  // that is two bad numbers is applied and reported by the verdict, because refusing there would put
  // this mirror of the server's rule in the way of a save the server itself allows.
  const applyDisabled = locked || documentUnreadable || (selectedStyle !== undefined && draftSize === undefined);
  const measureDisabled = measuring || measurementTrigger === undefined;

  const previewTiles =
    template === undefined || measurement?.status === "blocked" ? [] : samples.map((sample) => ({ sample, url: resolveAssetUrl(template, sample) }));
  const previewRatio = draftSize !== undefined && draftSize.width > 0 && draftSize.height > 0 ? draftSize.height / draftSize.width : undefined;

  return (
    <CardBox title={t("enforced-thumbnail-style.title", undefined, "Enforced thumbnail style")}>
      <div data-testid="enforced-style-control">
        <p className="text-muted">
          {t(
            "enforced-thumbnail-style.description",
            undefined,
            "Force every casino thumbnail to one style. The style needs the pixel size of its source images, which the players' lobby uses to shape the tiles.",
          )}
        </p>

        <DocumentNotices
          locked={locked}
          centralImagesEnabled={centralImagesEnabled}
          hasStyles={styleOptions.some((option) => option.usable)}
          documentUnreadable={documentUnreadable}
        />

        {!documentUnreadable && (
          <>
            <div className="form-group">
              <label htmlFor="enforced-style-picker">{t("enforced-thumbnail-style.style-label", undefined, "Enforced style")}</label>
              <select
                id="enforced-style-picker"
                className="form-control"
                data-testid="enforced-style-picker"
                disabled={locked}
                value={current.style}
                onChange={(event) => changeStyle(event.target.value)}
              >
                <option value={NO_STYLE}>{t("enforced-thumbnail-style.style-none", undefined, "None — no style enforced")}</option>
                {styleOptions.map(({ style, usable }) => (
                  <option
                    key={style}
                    value={style}
                  >
                    {usable ? style : t("enforced-thumbnail-style.style-without-template", { style }, `${style} — no image url template`)}
                  </option>
                ))}
              </select>
            </div>

            {selectedStyle !== undefined && (
              <>
                <div className="form-row">
                  <div className="form-group col-sm-3">
                    <label htmlFor="enforced-style-width">{t("enforced-thumbnail-style.width-label", undefined, "Image width (px)")}</label>
                    <input
                      id="enforced-style-width"
                      type="number"
                      min={1}
                      max={MAX_SIZE}
                      step={1}
                      className="form-control"
                      data-testid="enforced-style-width"
                      disabled={locked}
                      value={current.width}
                      onChange={(event) => updateDraftFromOperator((previous) => ({ ...previous, width: event.target.value }))}
                    />
                  </div>
                  <div className="form-group col-sm-3">
                    <label htmlFor="enforced-style-height">{t("enforced-thumbnail-style.height-label", undefined, "Image height (px)")}</label>
                    <input
                      id="enforced-style-height"
                      type="number"
                      min={1}
                      max={MAX_SIZE}
                      step={1}
                      className="form-control"
                      data-testid="enforced-style-height"
                      disabled={locked}
                      value={current.height}
                      onChange={(event) => updateDraftFromOperator((previous) => ({ ...previous, height: event.target.value }))}
                    />
                  </div>
                </div>

                <button
                  className="btn btn-secondary mb-3"
                  type="button"
                  data-testid="enforced-style-measure"
                  disabled={measureDisabled}
                  onClick={() => void runMeasurement()}
                >
                  {measuring
                    ? t("enforced-thumbnail-style.measuring", undefined, "Reading image sizes…")
                    : t("enforced-thumbnail-style.measure", undefined, "Read the image sizes again")}
                </button>

                {sizeRefused && (
                  <div
                    className="alert alert-warning"
                    role="alert"
                    data-testid="enforced-style-size-refused"
                  >
                    {t(
                      "enforced-thumbnail-style.size-refused",
                      { maximum: MAX_SIZE },
                      `Image sizes are whole numbers of pixels, up to ${MAX_SIZE}. Enter each size as a whole number in that range before applying.`,
                    )}
                  </div>
                )}
              </>
            )}

            <MeasurementNotices
              measurement={measurement}
              draftSize={draftSize}
              samplesFailed={samplesFailed}
              styleHasNoSamples={selectedStyle !== undefined && candidates !== undefined && samples.length === 0}
              readingPending={readingPending}
              locked={locked}
              onAdopt={adoptDimensions}
            />

            <VerdictNotice verdict={verdict.kind === "unusable" ? verdict : undefined} />

            {previewRatio !== undefined && previewTiles.length > 0 && (
              <TilePreview
                tiles={previewTiles}
                ratio={previewRatio}
              />
            )}
          </>
        )}

        <button
          className="btn btn-primary"
          type="button"
          data-testid="enforced-style-apply"
          disabled={applyDisabled}
          onClick={applySelection}
        >
          {t("enforced-thumbnail-style.apply", undefined, "Apply to document")}
        </button>
      </div>
    </CardBox>
  );
}
